// src/pages/AllProductsPage.js
import React, { useContext, useMemo, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import ProductCard from "../components/ProductCard";
import TopBanner from "../components/TopBanner";
import MainHeader from "../components/MainHeader";
import CartDrawer from "../components/CartDrawer";
import AddedToBagToast from "../components/AddedToBagToast";
import { getAllProducts } from "../services/products.api";
import { mapBackendProduct } from "../utils/mapProduct";
import { getPublicCategories } from "../services/categories.api";
import { useSeo } from "../hooks/useSeo";

function DrawerSection({ title, children }) {
  const [open, setOpen] = useState(true);

  return (
    <div className="border-b border-[#2E5A2A]/25 py-3">
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between text-sm font-semibold tracking-wide text-[#D6E5D9]"
      >
        <span>{title}</span>
        <span className="text-xs opacity-70">{open ? "▲" : "▼"}</span>
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

function AllProductsPage() {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();

 useSeo(
  "all-products",
  null,
  typeof window !== "undefined"
    ? {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name:
          lang === "EN"
            ? "All Tactical & Hunting Products"
            : "كل المنتجات التكتيكية",
        url: window.location.href
      }
    : null
);

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const isIOS =
    typeof navigator !== "undefined" &&
    /iPad|iPhone|iPod/.test(navigator.userAgent);

  const PAGE_SIZE = isIOS ? 14 : isMobile ? 18 : 50;

  const [products, setProducts] = useState([]);
  const [allVariantProducts, setAllVariantProducts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [categoryFilter, setCategoryFilter] = useState([]);
  const [sizeFilter, setSizeFilter] = useState([]);
  const [colorFilter, setColorFilter] = useState([]);
  
  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(5000);

  const [sortOption, setSortOption] = useState("bestSelling");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [gridMode, setGridMode] = useState("two");

  const [currentPage, setCurrentPage] = useState(1);
  
  const [showWhatsappCTA, setShowWhatsappCTA] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const TEXT = {
  EN: {
    filters: "Filters",
    category: "Category",
    size: "Size",
    color: "Color",
    price: "Price",
    heelHeight: "Heel height",
    all: "All",
    low: "Low",
    mid: "Mid",
    high: "High",
    appliedFilters: "Applied filters",
    clear: "Clear",
    clearAll: "Clear all",
    apply: "Apply",
  },
  AR: {
    filters: "تصفية",
    category: "الفئة",
    size: "المقاس",
    color: "اللون",
    price: "السعر",
    heelHeight: "ارتفاع الكعب",
    all: "الكل",
    low: "منخفض",
    mid: "متوسط",
    high: "عالٍ",
    appliedFilters: "الفلاتر المفعّلة",
    clear: "مسح",
    clearAll: "مسح الكل",
    apply: "تطبيق",
  },
}[lang];

  const CATEGORY_OPTIONS = (allCategories || []).map((c) => ({
  key: c.slug,
  labelEN: c.name_en,
  labelAR: c.name_ar,
}));

const appliedFilters = [];

if (categoryFilter.length) {
  const labels = CATEGORY_OPTIONS.filter((c) =>
    categoryFilter.includes(c.key)
  )
    .map((c) => (lang === "EN" ? c.labelEN : c.labelAR))
    .join(", ");

  appliedFilters.push({
    id: "category",
    label: `${TEXT.category}: ${labels}`,
    onClear: () => setCategoryFilter([]),
  });
}


if (colorFilter.length) {
  appliedFilters.push({
    id: "color",
    label: `${TEXT.color}: ${colorFilter.join(", ")}`,
    onClear: () => setColorFilter([]),
  });
}

if (priceMin > 0 || priceMax < 5000) {
  appliedFilters.push({
    id: "price",
    label: `${TEXT.price}: ${priceMin} – ${priceMax}`,
    onClear: () => {
      setPriceMin(0);
      setPriceMax(5000);
    },
  });
}

  useEffect(() => {
    getAllProducts({})
      .then((data) => {
        const mapped = data.map(mapBackendProduct);
        const safe = isMobile ? mapped.slice(0, 120) : mapped;
        setProducts(safe);
        setAllVariantProducts(safe);
      })
      .finally(() => setLoading(false));
  }, [isMobile]);

  useEffect(() => {
    getPublicCategories().then(setAllCategories);
  }, []);

  useEffect(() => {
  const t = setTimeout(() => setShowWhatsappCTA(true), 1000);
  return () => clearTimeout(t);
}, []);

useEffect(() => {
  const onScroll = () => setShowScrollTop(window.scrollY > 300);
  window.addEventListener("scroll", onScroll);
  return () => window.removeEventListener("scroll", onScroll);
}, []);


const toggleCategory = (key) => {
  setCategoryFilter((prev) =>
    prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
  );
};

const clearAllFilters = () => {
  setCategoryFilter([]);
  setSizeFilter([]);
  setColorFilter([]);
  setPriceMin(0);
  setPriceMax(5000);
};

  const filtered = useMemo(() => {
    let r = [...products];

    if (categoryFilter.length)
      r = r.filter((p) =>
        p.categories?.some((c) => categoryFilter.includes(c))
      );

    if (sizeFilter.length)
      r = r.filter((p) =>
        p.sizes?.some((s) => sizeFilter.includes(s.size) && s.inStock)
      );

    if (colorFilter.length)
      r = r.filter((p) =>
        p.colors?.some((c) => colorFilter.includes(c.name))
      );


    r = r.filter((p) => {
      const finalPrice = p.discount
        ? Math.round(p.price * (1 - p.discount / 100))
        : p.price;
      return finalPrice >= priceMin && finalPrice <= priceMax;
    });

    if (sortOption === "priceLowHigh") r.sort((a, b) => a.price - b.price);
    if (sortOption === "priceHighLow") r.sort((a, b) => b.price - a.price);

    return r;
  }, [
  products,
  categoryFilter,
  sizeFilter,
  colorFilter,
  priceMin,
  priceMax,
  sortOption,
]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPage]);

  const allSizes = useMemo(() => {
  const set = new Set();

  (allVariantProducts || []).forEach((p) =>
    (p.sizes || []).forEach(({ size }) => {
      if (size === null || size === undefined) return;
      if (typeof size === "number" && Number.isNaN(size)) return;
      if (typeof size === "string" && size.trim() === "") return;
      set.add(size);
    })
  );

  const arr = Array.from(set);
  return arr.sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isNaN(na) || Number.isNaN(nb))
      return String(a).localeCompare(String(b));
    return na - nb;
  });
}, [allVariantProducts]);

const allColors = useMemo(() => {
  const c = [];
  (allVariantProducts || []).forEach((p) =>
    (p.colors || []).forEach((col) => {
      if (!c.find((x) => x.name === col.name)) c.push(col);
    })
  );
  return c;
}, [allVariantProducts]);


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#9FB39C]">
        Loading…
      </div>
    );
  }

  return (
    <div className="bg-[#0F1A12] min-h-screen text-white">
      <TopBanner />
      <MainHeader />
      <CartDrawer />
      <AddedToBagToast />

      <div className="pt-[95px] px-4 pb-24">
        <button
          onClick={() => navigate(-1)}
          className="absolute top-[110px] left-4 text-3xl opacity-70"
        >
          ×
        </button>

        <h1 className="text-xl font-bold text-center tracking-widest">
          {lang === "EN" ? "ALL PRODUCTS" : "كل المنتجات"}
        </h1>
        <div className="w-20 h-[2px] bg-[#2E5A2A] mx-auto my-4" />

        <div className="flex gap-2 mb-3">
  {/* FILTER */}
  <button
    onClick={() => setIsFilterOpen(true)}
    className="
      flex-1 flex items-center justify-center gap-2
      bg-[#132117]
      border border-[#2E5A2A]/50
      hover:bg-[#1B2F22]
      transition
      rounded-md
      py-2.5 text-xs font-semibold tracking-wide
    "
  >
    <span>{lang === "EN" ? "FILTERS" : "تصفية"}</span>
    <span className="flex flex-col gap-[2px]">
      <span className="w-3 h-[1.5px] bg-white rounded-full" />
      <span className="w-4 h-[1.5px] bg-white rounded-full" />
      <span className="w-2 h-[1.5px] bg-white rounded-full" />
    </span>
  </button>

  {/* SORT */}
<div className="relative flex-1">
  <button
    onClick={() => setIsSortOpen((p) => !p)}
    className="
      w-full flex items-center justify-center gap-2
      rounded-md
      py-2.5 text-xs font-semibold tracking-wide
      bg-[#132117]
      border border-[#2E5A2A]/50
      hover:bg-[#1B2F22]
      transition
    "
  >
    <span>{lang === "EN" ? "SORT BY" : "ترتيب حسب"}</span>
    <span className="text-[10px]">{isSortOpen ? "▲" : "▼"}</span>
  </button>

  {isSortOpen && (
    <div
      className="
        absolute right-0 mt-1 w-44
        bg-[#132117]
        border border-[#2E5A2A]/50
        rounded-md shadow-lg z-20 text-xs
      "
      style={lang === "AR" ? { left: 0, right: "auto" } : {}}
    >
      <button
        onClick={() => {
          setSortOption("bestSelling");
          setCurrentPage(1);
          setIsSortOpen(false);
        }}
        className={`w-full text-left px-3 py-2 hover:bg-[#2E5A2A]/20 ${
          sortOption === "bestSelling"
            ? "font-semibold text-[#2E5A2A]"
            : ""
        }`}
      >
        {lang === "EN" ? "Best Selling" : "الأكثر مبيعًا"}
      </button>

      <button
        onClick={() => {
          setSortOption("priceLowHigh");
          setCurrentPage(1);
          setIsSortOpen(false);
        }}
        className={`w-full text-left px-3 py-2 hover:bg-[#2E5A2A]/20 ${
          sortOption === "priceLowHigh"
            ? "font-semibold text-[#2E5A2A]"
            : ""
        }`}
      >
        {lang === "EN" ? "Price: Low to High" : "السعر: من الأقل للأعلى"}
      </button>

      <button
        onClick={() => {
          setSortOption("priceHighLow");
          setCurrentPage(1);
          setIsSortOpen(false);
        }}
        className={`w-full text-left px-3 py-2 hover:bg-[#2E5A2A]/20 ${
          sortOption === "priceHighLow"
            ? "font-semibold text-[#2E5A2A]"
            : ""
        }`}
      >
        {lang === "EN" ? "Price: High to Low" : "السعر: من الأعلى للأقل"}
      </button>
    </div>
  )}
</div>
</div>

<div className="flex items-center justify-between mb-4">
  <span className="text-[11px] text-white/60">
    {filtered.length} {lang === "EN" ? "products" : "منتج"}
  </span>

  <div className="flex items-center gap-1">
  <button
    onClick={() => setGridMode("two")}
    className={`
      w-7 h-7 border rounded-md flex items-center justify-center
      ${
        gridMode === "two"
          ? "bg-[#2E5A2A] text-white border-[#2E5A2A]"
          : "border-white/30"
      }
    `}
    aria-label="Grid 2 per row"
  >
    <div className="grid grid-cols-2 grid-rows-2 gap-[2px] w-3 h-3">
      <span className="bg-current" />
      <span className="bg-current" />
      <span className="bg-current" />
      <span className="bg-current" />
    </div>
  </button>

  <button
    onClick={() => setGridMode("one")}
    className={`
      w-7 h-7 border rounded-md flex items-center justify-center
      ${
        gridMode === "one"
          ? "bg-[#2E5A2A] text-white border-[#2E5A2A]"
          : "border-white/30"
      }
    `}
    aria-label="Grid 1 per row"
  >
    <div className="flex flex-col gap-[2px] w-3">
      <span className="h-[3px] bg-current rounded-sm" />
      <span className="h-[3px] bg-current rounded-sm" />
      <span className="h-[3px] bg-current rounded-sm" />
    </div>
  </button>
</div>

</div>


        {/* GRID */}
        <div
          className={`grid ${
            gridMode === "two"
              ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
              : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
          } gap-3`}
        >
          {paginated.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  setCurrentPage(i + 1);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className={`w-9 h-9 text-xs font-semibold border ${
                  currentPage === i + 1
                    ? "bg-[#2E5A2A]"
                    : "border-[#2E5A2A]"
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ================= FILTER DRAWER (LEFT) ================= */}
<div
  className={`
    fixed inset-0 z-[9999]
    transition-opacity duration-300
    ${isFilterOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}
  `}
>
  {/* BACKDROP */}
  <div
    className="absolute inset-0 bg-black/60"
    onClick={() => setIsFilterOpen(false)}
  />

  {/* PANEL */}
  <div
    className={`
      absolute top-0 bottom-0 left-0
      w-[90%] max-w-sm
      bg-gradient-to-b from-[#0F1A12] to-[#0B140E]
text-white
border-r border-[#2E5A2A]/40
shadow-[4px_0_30px_rgba(0,0,0,0.7)]
      transform transition-transform duration-300
      ${isFilterOpen ? "translate-x-0" : "-translate-x-full"}
    `}
    dir={lang === "AR" ? "rtl" : "ltr"}
    style={lang === "AR" ? { left: "auto", right: 0 } : {}}
  >
    {/* HEADER */}
    <div className="flex items-center justify-between px-4 py-4
border-b border-[#2E5A2A]/40
bg-[#101C14]
sticky top-0 z-10">

      <span className="text-sm font-semibold">
        {TEXT.filters}
      </span>
      <button
        onClick={() => setIsFilterOpen(false)}
        className="text-2xl opacity-70 hover:opacity-100"
      >
        ×
      </button>
    </div>

    {/* CONTENT */}
    <div className="h-[calc(100%-110px)] overflow-y-auto px-4 pt-3 pb-24 text-sm">

      {/* APPLIED FILTERS */}
      {appliedFilters.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-[#9EFFA1]">
              {TEXT.appliedFilters}
            </span>
            <button
              onClick={clearAllFilters}
              className="text-[11px] underline text-white/60 hover:text-white"
            >
              {TEXT.clearAll}
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {appliedFilters.map((f) => (
              <button
                key={f.id}
                onClick={f.onClear}
                className="
                  flex items-center gap-1
                  px-2 py-[3px]
                  rounded-full
                  bg-[#132117]
border border-[#2E5A2A]/50
hover:bg-[#1B2F22]
transition

                  text-[11px]
                "
              >
                <span>{f.label}</span>
                <span>×</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* CATEGORY */}
      <DrawerSection title={TEXT.category}>
        <div className="space-y-2 mt-2">
          {CATEGORY_OPTIONS.map((c) => {
            const checked = categoryFilter.includes(c.key);
            return (
              <label key={c.key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleCategory(c.key)}
                  className="w-4 h-4 accent-[#2E5A2A]"
                />
                <span>{lang === "EN" ? c.labelEN : c.labelAR}</span>
              </label>
            );
          })}
        </div>
      </DrawerSection>

      {/* COLOR */}
      <DrawerSection title={TEXT.color}>
        <div className="flex flex-wrap gap-3 mt-2">
          {allColors.map((c) => (
            <button
              key={c.name}
              onClick={() =>
                setColorFilter((prev) =>
                  prev.includes(c.name)
                    ? prev.filter((x) => x !== c.name)
                    : [...prev, c.name]
                )
              }
              className="flex flex-col items-center gap-1 text-[10px]"
            >
              <span
                className={`
                  w-7 h-7 rounded-full border
                  ${colorFilter.includes(c.name)
                    ? "border-[#9EFFA1]"
                    : "border-white/30"}
                `}
                style={{ backgroundColor: c.code }}
              />
              <span className="text-white/70">{c.name}</span>
            </button>
          ))}
        </div>
      </DrawerSection>

      {/* PRICE */}
      <DrawerSection title={TEXT.price}>
        <div className="mt-3 space-y-3">
          <div className="flex gap-3">
            <input
              type="number"
              value={priceMin}
              onChange={(e) => setPriceMin(Number(e.target.value) || 0)}
              className="w-full bg-[#121F17] bg-[#132117]
border border-[#2E5A2A]/50
hover:bg-[#1B2F22]
transition
 rounded px-2 py-1 text-xs"
            />
            <input
              type="number"
              value={priceMax}
              onChange={(e) => setPriceMax(Number(e.target.value) || 0)}
              className="w-full bg-[#121F17] bg-[#132117]
border border-[#2E5A2A]/50
hover:bg-[#1B2F22]
transition
rounded px-2 py-1 text-xs"
            />
          </div>
        </div>
      </DrawerSection>
      {/* EXPLORE ALL */}
<div className="mt-6 px-1">
  <button
    onClick={() => {
      setIsFilterOpen(false);
      navigate("/all-products");
    }}
    className="
      w-full py-3
      rounded-md
      text-xs font-semibold tracking-widest
      border border-[#2E5A2A]/50
      text-[#9EFFA1]
      hover:bg-[#2E5A2A]/20
      transition
    "
  >
    {lang === "EN" ? "EXPLORE ALL PRODUCTS" : "تصفح كل المنتجات"}
  </button>
</div>
    </div>

    {/* FOOTER BUTTONS */}
    <div className="absolute bottom-0 left-0 right-0 border-t border-[#2E5A2A]/30 bg-[#0F1A12] px-4 py-4 flex gap-3 bg-[#0C1610]
">
      <button
        onClick={clearAllFilters}
        className="flex-1 py-2 border border-white/30 rounded-md text-xs"
      >
        {TEXT.clear.toUpperCase()}
      </button>
      <button
        onClick={() => setIsFilterOpen(false)}
        className="flex-1 py-2 rounded-md text-xs bg-[#2E5A2A]"
      >
        {TEXT.apply.toUpperCase()}
      </button>
    </div>
  </div>
</div>
{/* WHATSAPP CTA */}
{showWhatsappCTA && (
  <div className="fixed z-[9999] bottom-20 right-5 flex flex-col items-center gap-2">
    <button
      onClick={() => setShowWhatsappCTA(false)}
      className="w-7 h-7 rounded-full bg-white text-black text-sm"
    >
      ×
    </button>
    <a
      href="https://wa.me/96170203205"
      target="_blank"
      rel="noreferrer"
      className="w-14 h-14 rounded-full flex items-center justify-center"
      style={{ backgroundColor: "#25D366" }}
    >
      <img
        src="/icons/whatsapp.png"
        alt="WhatsApp"
        className="w-7 h-7"
      />
    </a>
  </div>
)}

{showScrollTop && (
  <button
    onClick={() =>
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
    className="fixed top-24 left-1/2 -translate-x-1/2 w-12 h-12 bg-white text-black rounded-full"
  >
    ↑
  </button>
)}

    </div>
  );
}


export default AllProductsPage;
